import type { RefObject } from "react";
import { useState } from "react";

/**
 * Buttons that write the formatting for you.
 *
 * The stored value is still plain text — these only save people from
 * remembering the punctuation. Someone who already knows it can type it, and
 * someone who does not never has to learn.
 */
export function FormatBar({
  target,
  value,
  onChange,
}: {
  target: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  // The selection at the moment Link was pressed. Opening the dialog moves
  // focus out of the textarea, which loses it.
  const [linkAt, setLinkAt] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const apply = (
    fn: (
      sel: string,
      before: string,
      after: string,
    ) => { text: string; select: [number, number] },
  ): void => {
    const el = target.current;
    if (!el) return;

    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const { text, select } = fn(
      value.slice(start, end),
      value.slice(0, start),
      value.slice(end),
    );

    onChange(text);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(select[0], select[1]);
    });
  };

  /**
   * Wraps the selection and LEAVES IT SELECTED.
   *
   * That is what makes bold and italic combine: wrapping "text" gives
   * "**text**" with "text" still selected, so pressing italic next wraps the
   * same words again and yields "***text***". Collapsing the caret instead
   * meant the second button had nothing to act on.
   */
  const wrap = (mark: string, placeholder: string) =>
    apply((sel, before, after) => {
      const body = sel || placeholder;
      const text = `${before}${mark}${body}${mark}${after}`;
      const inner = before.length + mark.length;
      return { text, select: [inner, inner + body.length] };
    });

  /**
   * Prefixes the selected lines, continuing any numbering already above.
   *
   * Pressing the numbered-list button twice used to give "1." and then "1."
   * again, because it never looked at what came before.
   */
  const prefixLines = (make: (i: number, startFrom: number) => string) =>
    apply((sel, before, after) => {
      const needsBreak = before !== "" && !before.endsWith("\n");
      const lead = needsBreak ? "\n" : "";

      // The number to carry on from, if the line above is already numbered.
      const previous = before.replace(/\n$/, "").split("\n").pop() ?? "";
      const seen = /^\s*(\d+)[.)]\s/.exec(previous);
      const startFrom = seen ? Number(seen[1]) + 1 : 1;

      const lines = sel ? sel.split("\n") : [""];
      const marked = lines.map((l, i) => `${make(i, startFrom)}${l}`).join("\n");
      const text = `${before}${lead}${marked}${after}`;
      const caret = before.length + lead.length + marked.length;
      return { text, select: [caret, caret] };
    });

  const openLink = (): void => {
    const el = target.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    setLinkAt({ start, end });
    setLinkText(value.slice(start, end));
    setLinkUrl("");
    setLinkOpen(true);
  };

  const insertLink = (): void => {
    const url = linkUrl.trim();
    if (url === "") return;
    // A bare "example.com" is what people paste; make it a real link rather
    // than something the renderer will refuse.
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const label = linkText.trim() || href;

    const snippet = `[${label}](${href})`;
    const text = `${value.slice(0, linkAt.start)}${snippet}${value.slice(linkAt.end)}`;
    onChange(text);
    setLinkOpen(false);

    const el = target.current;
    const caret = linkAt.start + snippet.length;
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="formatbar-wrap">
      <div className="formatbar">
        <button type="button" title="Bold" onClick={() => wrap("**", "bold text")}>
          <strong>B</strong>
        </button>
        <button type="button" title="Italic" onClick={() => wrap("*", "italic text")}>
          <em>I</em>
        </button>
        <button
          type="button"
          title="Bulleted list"
          onClick={() => prefixLines(() => "- ")}
        >
          • List
        </button>
        <button
          type="button"
          title="Numbered list — continues from the line above"
          onClick={() => prefixLines((i, from) => `${from + i}. `)}
        >
          1. List
        </button>
        <button
          type="button"
          title="Checklist — these can be ticked off later"
          onClick={() => prefixLines(() => "- [ ] ")}
        >
          ☐ Checklist
        </button>
        <button type="button" title="Insert a link" onClick={openLink}>
          Link
        </button>
      </div>

      {linkOpen ? (
        <div className="link-pop">
          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor="lk-text">Text to show</label>
            <input
              id="lk-text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Scope memo"
              autoFocus
            />
          </div>
          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor="lk-url">Link address</label>
            <input
              id="lk-url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="example.com/scope"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  insertLink();
                }
                if (e.key === "Escape") setLinkOpen(false);
              }}
            />
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" onClick={() => setLinkOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="primary"
              onClick={insertLink}
              disabled={linkUrl.trim() === ""}
            >
              Insert link
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Carries a list on to the next line when Enter is pressed.
 *
 * Without this the buttons are the only way to make a second item, which is
 * why a numbered list kept coming out as a row of "1."s. Pressing Enter on an
 * item that is still empty ends the list instead, which is how people expect
 * to stop.
 *
 * Returns null when the line is not a list item, leaving Enter alone.
 */
export function continueList(
  value: string,
  caret: number,
): { text: string; caret: number } | null {
  const before = value.slice(0, caret);
  const line = before.slice(before.lastIndexOf("\n") + 1);

  const check = /^(\s*)([-*])\s+\[[ xX]?\]\s+(.*)$/.exec(line);
  const numbered = /^(\s*)(\d+)([.)])\s+(.*)$/.exec(line);
  const bullet = /^(\s*)([-*])\s+(.*)$/.exec(line);

  let marker: string | null = null;
  let content = "";

  if (check) {
    marker = `${check[1]}${check[2]} [ ] `;
    content = check[3] ?? "";
  } else if (numbered) {
    marker = `${numbered[1]}${Number(numbered[2]) + 1}${numbered[3]} `;
    content = numbered[4] ?? "";
  } else if (bullet) {
    marker = `${bullet[1]}${bullet[2]} `;
    content = bullet[3] ?? "";
  }

  if (marker === null) return null;

  // Enter ALWAYS continues, even on an item nothing has been typed into.
  //
  // The usual editor rule is that Enter on an empty item ends the list and
  // removes its marker. It reads as the app deleting what you just typed —
  // press the numbered-list button, hit Enter out of habit, and the "3."
  // vanishes. Nothing here should ever silently remove someone's text; a
  // stray empty marker is trivially backspaced away, a lost one is a moment
  // of "where did that go".
  void content;

  const text = `${before}\n${marker}${value.slice(caret)}`;
  return { text, caret: caret + 1 + marker.length };
}
